import type { ErrorLike } from '@apollo/client';
import {
  BodyText,
  Box,
  Flex,
  Heading,
  Strong,
} from '@utilitywarehouse/hearth-react';
import {
  SpreedlyEnvironmentType,
  SpreedlyTokenisationComponent,
} from '@utilitywarehouse/spreedly-tokenisation-component';
import { useEffect } from 'react';

import { SPREEDLY_ENV } from '@constants/config';
import {
  CardPayment3DsChallenge,
  CardPaymentDeclined,
  PaymentDetails,
  PaymentSubmissionOutcome,
} from '@generated/gql/graphql';
import { useLoader } from '@hooks/useLoader';
import { RequestState } from '@src/types/index';
import { formatCurrency } from '@utils/format';
import { useBreakpoints } from '@utils/useBreakpoints';

import { DepositErrorComponent } from '../Error';
import { ProcessPayment } from '../ProcessPayment';
import { getHandleSpreedlyResponse } from '../Spreedly/handleSpreedleyComponentResponse';

type DepositDetailsProps = {
  basketID: string;
  depositDataCurrency: string;
  depositDataValue: string;
  submitDeposit: (
    basketId: string,
    paymentDetails: PaymentDetails
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  submitDepositData?: {
    submitDeposit: PaymentSubmissionOutcome;
  } | null;
  submitDepositError?: ErrorLike;
  submitDepositLoading: boolean;
  redirectTransactionToken?: string;
};

const isCardPaymentDeclined = (
  submitDeposit: PaymentSubmissionOutcome
): submitDeposit is CardPaymentDeclined =>
  submitDeposit?.__typename === 'CardPaymentDeclined';

const isCardPayment3DsChallenge = (
  submitDeposit: PaymentSubmissionOutcome
): submitDeposit is CardPayment3DsChallenge =>
  submitDeposit?.__typename === 'CardPayment3DSChallenge';

const DepositDetailsForm = ({
  submitDeposit,
  submitDepositError,
  submitDepositLoading,
  submitDepositData,
  depositDataValue,
  depositDataCurrency,
  basketID,
  redirectTransactionToken,
}: DepositDetailsProps) => {
  const formattedAmount = formatCurrency({
    value: depositDataValue,
    currency: depositDataCurrency,
  });
  const { setRequestState } = useLoader();
  const { breakpointsDown } = useBreakpoints();

  const isMobileOrTablet = breakpointsDown.md;

  useEffect(() => {
    if (submitDepositLoading) {
      return setRequestState(RequestState.loading);
    }
    return setRequestState(RequestState.success);
  }, [submitDepositData, setRequestState, submitDepositLoading]);

  if (
    submitDepositError ||
    (submitDepositData &&
      isCardPaymentDeclined(submitDepositData.submitDeposit) &&
      submitDepositData?.submitDeposit?.error)
  ) {
    return <DepositErrorComponent message={submitDepositError?.message} />;
  }

  if (submitDepositLoading) {
    return null;
  }

  if (!submitDepositData && !redirectTransactionToken) {
    return (
      <Flex direction="column" spacing="lg" maxWidth="680px">
        <BodyText>
          Before we can complete your order, we need to take a payment of{' '}
          <Strong>{formattedAmount}</Strong>. You’ll see this added to your UW
          account balance after you’ve signed up, and you’ll be able to withdraw
          it back to your bank account once you’ve been with us for 12 months.
        </BodyText>
        <Heading as="h3" size="md">
          Make your payment of {formattedAmount}
        </Heading>
        <Box id="spreedly-iframe">
          <SpreedlyTokenisationComponent
            spreedlyEnvironment={SPREEDLY_ENV as SpreedlyEnvironmentType}
            onReturnSpreedlyResponse={getHandleSpreedlyResponse(
              submitDeposit,
              basketID,
              isMobileOrTablet
            )}
            buttonLabel="Pay and confirm"
            buttonContainerProps={{
              justifyContent: 'end',
            }}
          />
        </Box>
      </Flex>
    );
  }

  let submitDepositResponseTransactionData: string = '{}';
  let submitDepositResponseExternalTransactionToken: string = '';

  if (
    submitDepositData &&
    isCardPayment3DsChallenge(submitDepositData.submitDeposit)
  ) {
    const { transactionData, externalTransactionToken } =
      submitDepositData.submitDeposit;
    submitDepositResponseTransactionData = transactionData;
    submitDepositResponseExternalTransactionToken = externalTransactionToken;
  }

  // If there is a redirectTransactionToken this should take precedence
  return (
    <ProcessPayment
      basketID={basketID}
      amount={depositDataValue}
      transactionData={JSON.parse(submitDepositResponseTransactionData)}
      externalTransactionToken={
        redirectTransactionToken ||
        submitDepositResponseExternalTransactionToken
      }
    />
  );
};

export { DepositDetailsForm };
